import * as React from 'react';

import { DecoratorNode } from '@fluentui-copilot/text-editor';
import type {
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from '@fluentui-copilot/text-editor';
import type { JSXElement } from '@fluentui/react-utilities';
import type { ChatInputEntityProps } from '../../ChatInputEntity';
import { ChatInputEntity } from '../../ChatInputEntity';
import type { ChatInputEntityData } from '@fluentui-copilot/chat-input-plugins';

export type SerializedChatInputEntityNode<T> = Spread<
  { pluginId: string; text: string; data?: T; entityProps?: ChatInputEntityProps },
  SerializedLexicalNode
>;

export class ChatInputEntityNode<T> extends DecoratorNode<JSXElement | null> {
  __pluginId: string;
  __text: string;
  __entityProps?: ChatInputEntityProps;
  __data?: T;

  static getType(): string {
    return 'entity';
  }
  static clone<T>(node: ChatInputEntityNode<T>): ChatInputEntityNode<T> {
    return new ChatInputEntityNode(node.__pluginId, node.__text, node.__data, node.__entityProps, node.__key);
  }

  static importJSON<T>(serializedNode: SerializedChatInputEntityNode<T>): ChatInputEntityNode<T> {
    return $createChatInputEntityNode(
      serializedNode.pluginId,
      serializedNode.text,
      serializedNode.data,
      serializedNode.entityProps,
    );
  }

  isIsolated(): boolean {
    return false;
  }
  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return false;
  }

  getTextContent(): string {
    return this.__text;
  }

  exportJSON(): SerializedChatInputEntityNode<T> {
    return {
      pluginId: this.__pluginId,
      text: this.__text,
      data: this.__data,
      entityProps: this.__entityProps,
      type: ChatInputEntityNode.getType(),
      version: 1,
    };
  }

  constructor(pluginId: string, text: string, data?: T, entityProps?: ChatInputEntityProps, key?: NodeKey) {
    super(key);
    this.__pluginId = pluginId;
    this.__text = text;
    this.__data = data;
    this.__entityProps = entityProps;
  }

  createDOM(): HTMLElement {
    // eslint-disable-next-line no-restricted-globals
    return document.createElement('span');
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSXElement | null {
    if (this.__entityProps?.children) {
      return <ChatInputEntity {...this.__entityProps} />;
    }
    return <ChatInputEntity {...this.__entityProps}>{this.__text}</ChatInputEntity>;
  }

  getEntityData(): ChatInputEntityData<T, ChatInputEntityProps> {
    return {
      text: this.__text,
      data: this.__data,
      entityProps: this.__entityProps,
    };
  }

  updateEntityData(data: ChatInputEntityData<T, ChatInputEntityProps>): void {
    const writable = this.getWritable();
    writable.__text = data.text;
    writable.__data = data.data;
    writable.__entityProps = data.entityProps;
  }
}

export function $createChatInputEntityNode<T>(
  pluginId: string,
  text: string,
  data?: T,
  entityProps?: ChatInputEntityProps,
  key?: NodeKey,
): ChatInputEntityNode<T> {
  return new ChatInputEntityNode(pluginId, text, data, entityProps, key);
}

export function $isChatInputEntityNode<T>(node: LexicalNode | null | undefined): node is ChatInputEntityNode<T> {
  return node instanceof ChatInputEntityNode;
}
